import { createSharedComposable } from '@vueuse/core'
import { computed, ref } from 'vue'
import { getUXStatus } from '~/logic/connectionStatus'
import type { UXConnectionStatus } from '~/logic/connectionStatus'
import type {
  ConnectionMetadata,
  ConnectionRegion,
} from '~/models/connectionMetadata'
import { useConnectionsStore } from '~/stores/connections'

// Category filtering (terminals / appointment systems / shipping lines) was
// dropped from the filter strip because the page already groups by category
// via sub-section headers — a chip for the same axis would be redundant.
export type FilterChip = 'all' | 'needs_attention' | 'paused' | 'not_used'

export type RegionFilter = 'all' | ConnectionRegion

export interface ConnectionCounts {
  connected: number
  failing: number
  not_set_up: number
  paused: number
  not_used: number
  total: number
}

function matchesChip(status: UXConnectionStatus, chip: FilterChip): boolean {
  switch (chip) {
    case 'all':
      return true
    case 'needs_attention':
      return status === 'failing' || status === 'not_set_up'
    case 'paused':
      return status === 'paused'
    case 'not_used':
      return status === 'not_used'
  }
}

function _useConnectionsFilter() {
  const connectionsStore = useConnectionsStore()
  const chip = ref<FilterChip>('all')
  const region = ref<RegionFilter>('all')
  const search = ref('')

  function setChip(c: FilterChip): void {
    chip.value = c
  }

  function setRegion(r: RegionFilter): void {
    region.value = r
  }

  function setSearch(s: string): void {
    search.value = s
  }

  /** Call in Storybook story setup() to prevent filter state leaking between stories. */
  function reset(): void {
    chip.value = 'all'
    region.value = 'all'
    search.value = ''
  }

  const counts = computed((): ConnectionCounts => {
    const result: ConnectionCounts = {
      connected: 0,
      failing: 0,
      not_set_up: 0,
      paused: 0,
      not_used: 0,
      total: 0,
    }
    // Popup tiles use this. A covered row stays on All, but it is not setup
    // work, so it must not land in failing, not_set_up, or total.
    for (const meta of connectionsStore.setupConnectionTypeMetas) {
      const connection = connectionsStore.connectionLookup.get(meta.type)
      const status = getUXStatus(connection)
      result[status]++
      result.total++
    }
    return result
  })

  const filteredMetas = computed((): ConnectionMetadata[] => {
    const searchLower = search.value.toLowerCase().trim()
    return connectionsStore.editableConnectionTypeMetasForOrg.filter((meta) => {
      // All still shows a covered row. Needs attention skips it once the
      // covering connection is Validated.
      if (
        chip.value === 'needs_attention' &&
        connectionsStore.isSetupCovered(meta.type)
      ) {
        return false
      }
      const connection = connectionsStore.connectionLookup.get(meta.type)
      const status = getUXStatus(connection)
      if (!matchesChip(status, chip.value)) return false
      if (region.value !== 'all' && !meta.regions?.includes(region.value))
        return false
      if (searchLower && !meta.label.toLowerCase().includes(searchLower))
        return false
      return true
    })
  })

  return {
    chip,
    region,
    search,
    setChip,
    setRegion,
    setSearch,
    filteredMetas,
    counts,
    reset,
  }
}

export const useConnectionsFilter = createSharedComposable(
  _useConnectionsFilter
)
